import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { CashbackService } from 'src/app/core/services/cashback.service';
import { DiscountCouponService } from 'src/app/core/services/discount-coupon.service';
import { RestaurantService } from 'src/app/core/services/restaurant.service';
import { ShoppingCartService } from 'src/app/core/services/shoppingCart.service';

/**
 * Pop-up do cupom em DESTAQUE do Cupom v2 (módulo new_coupon) no cardápio:
 * GET /discount-coupons/featured (o server garante no máximo 1 por loja). Abre sozinho
 * uma vez por sessão; depois vira o botão flutuante "Ver cupom" (canto inferior
 * ESQUERDO). "Resgatar cupom" deixa o código PENDENTE e vira, no mesmo card, a
 * confirmação "Cupom resgatado!", que NÃO fecha sozinha (doc Cupons 3.0 telas do
 * cliente, 3.3): o cliente fecha pelo × ou tocando fora. Sem a flag (ou sem destaque)
 * nada renderiza.
 *
 * A pílula "Ver cupom" existe SÓ na tela inicial do cardápio (doc 3.4, QA E02-E): ela é
 * position fixed e o ion-router-outlet EMPILHA as páginas (produto/pizza/extra abrem por
 * rota com o cardápio vivo por baixo), então sem o gate de rota ela flutuava sobre as
 * telas de montagem, cobrindo o rodapé PRÓXIMO.
 */
@Component({
    selector: 'app-featured-coupon-popup',
    templateUrl: './featured-coupon-popup.component.html',
    styleUrls: ['./featured-coupon-popup.component.scss'],
})
export class FeaturedCouponPopupComponent implements OnInit, OnDestroy {
    coupon: any = null;
    open = false;
    redeemed = false;
    onMenuHome = true;
    // Chip de saldo de cashback (balance-cashback): fab preto no MESMO canto da pill,
    // só no fluxo legado e com saldo. Quando ele existe, a pill sobe para empilhar os dois.
    cashbackChipVisible = false;

    private routerSub: Subscription = null;

    constructor(
        private discountCouponService: DiscountCouponService,
        private restaurantService: RestaurantService,
        private shoppingCartService: ShoppingCartService,
        private cashbackService: CashbackService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) {}

    /**
     * Tela inicial do cardápio = '/:restaurant' (redirect transitório para o menu) ou
     * '/:restaurant/menu' (a tab). Rota empilhada (pizza/extra/...) e as outras tabs
     * (prêmios/perfil/pedidos) ficam de fora.
     */
    static isMenuHomeUrl(url: string): boolean {
        const segments = String(url || '').split('?')[0].split('#')[0].split('/').filter(Boolean);

        if (segments.length <= 1) return true;

        return segments.length === 2 && segments[1] === 'menu';
    }

    // O carrinho flutuante do fluxo novo (.new-cart-bar) é FULL-WIDTH, fixado logo acima
    // da tabs-bar: com item no carrinho a pílula sobe para não cobri-lo. No fluxo legado
    // o carrinho é um FAB no canto DIREITO e não disputa com a pílula (esquerda).
    get cartBarVisible(): boolean {
        const products = this.shoppingCartService.products || [];
        const modules = this.restaurantService.restaurant && this.restaurantService.restaurant.info && this.restaurantService.restaurant.info.modules;

        return products.length > 0 && !!(modules && modules.new_flow_checkout_pedir_delivery);
    }

    ngOnInit() {
        if (!this.discountCouponService.isActive) return;

        // Mesma condição de exibição do balance-cashback: saldo > 0, nos DOIS fluxos.
        // Guest/erro de API = sem chip, pill no lugar.
        //
        // Havia aqui um gate que pulava esta consulta no fluxo novo, escrito quando o chip
        // de fato não era renderizado lá. O TV-919 devolveu o chip ao fluxo novo, então a
        // premissa deixou de valer: sem esta leitura a pílula acha que o canto está livre e
        // é pintada por cima do chip (ela é z-index 1500, o fab é 999).
        this.cashbackService.getCashbackBalance()
            .then((balance) => {
                this.cashbackChipVisible = !!(balance && balance.balance > 0);
                this.cdr.markForCheck(); // host OnPush: ver comentário no getFeatured
            })
            .catch(() => { /* sem saldo/sem login: chip não aparece */ });

        this.onMenuHome = FeaturedCouponPopupComponent.isMenuHomeUrl(this.router.url);
        this.routerSub = this.router.events.subscribe((event) => {
            if (!(event instanceof NavigationEnd)) return;

            this.onMenuHome = FeaturedCouponPopupComponent.isMenuHomeUrl(event.urlAfterRedirects || event.url);
            // detectChanges, NÃO markForCheck: ao empilhar uma página por cima, o Ionic
            // DESANEXA a página coberta da change detection, então o markForCheck nunca
            // seria descarregado enquanto o cardápio está por baixo, e a pill (fixed)
            // continuava pintada sobre a tela de cima (QA E02-E). detectChanges roda a
            // checagem LOCAL desta view mesmo com o ancestral desanexado.
            this.cdr.detectChanges();
        });

        this.discountCouponService.getFeatured().then((coupon) => {
            if (!coupon) return;

            this.coupon = coupon;
            this.redeemed = this.discountCouponService.peekPendingCode() === coupon.code;

            // Auto-abre só na primeira visita da sessão E sem cupom já escolhido: quem
            // chegou pelo link /c/<código> tem intenção definida, o pop-up não disputa
            // com o toast de chegada (e "Resgatar" aqui sobrescreveria o código do link).
            // O seenKey fica intacto nesse caso: o auto-open volta a valer quando o
            // pendente for consumido no pagamento. O botão "Ver cupom" segue disponível.
            if (!sessionStorage.getItem(this.seenKey()) && !this.discountCouponService.hasPendingCode) {
                sessionStorage.setItem(this.seenKey(), '1');
                this.open = true;
            }

            // A menu-list (host) é OnPush: num tick global ela é PULADA junto com esta
            // subárvore, então mudança vinda de promise/timer não chega ao DOM sozinha.
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    openPopup() {
        this.redeemed = this.discountCouponService.peekPendingCode() === this.coupon.code;
        this.open = true;
    }

    closePopup() {
        this.open = false;
    }

    redeem() {
        this.discountCouponService.setPendingCode(this.coupon.code);
        // A confirmação NÃO fecha sozinha (decisão do doc: fechamento automático foi
        // descartado): fica até o cliente fechar pelo × ou tocar fora.
        this.redeemed = true;
    }

    discountSentence(): string {
        if (!this.coupon) return '';
        if (this.coupon.discountType === 'free_shipping') return 'Frete grátis';
        if (this.coupon.discountType === 'percent') return `${this.trimNumber(this.coupon.discountValue)}% de desconto`;

        return `R$ ${this.trimNumber(this.coupon.discountValue)} de desconto`;
    }

    /**
     * Restrição PRINCIPAL do cupom, discreta no rodapé do card (doc 3.2): uma só, na
     * prioridade dos exemplos do doc, e permanece visível após o resgate — é ela que
     * explica por que o cupom ainda não entrou no pedido.
     */
    ruleSentence(): string {
        if (!this.coupon) return '';
        if (this.coupon.firstOrderOnly) return 'Disponível apenas para primeira compra';
        if (this.coupon.perCustomerLimit) return 'Disponível uma vez por cliente';
        if (this.coupon.minOrderValue) return `Válido para pedidos acima de R$ ${Number(this.coupon.minOrderValue).toFixed(2).replace('.', ',')}`;
        if (this.coupon.discountType === 'free_shipping') return 'Válido apenas para entrega';

        return '';
    }

    private trimNumber(value): string {
        const parsed = Number(value) || 0;

        return (parsed % 1 === 0 ? String(parsed) : parsed.toFixed(2).replace('.', ','));
    }

    private seenKey(): string {
        return `dc-featured-seen-${this.restaurantService.restaurant.info.id}-${this.coupon.code}`;
    }
}
